import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Lock, 
  Sparkles, 
  UserPlus, 
  ArrowRight, 
  Brain, 
  MessageCircle, 
  Heart, 
  Trophy, 
  Calendar,
  Bell,
  X,
  Zap
} from "lucide-react";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

// Feature cards data
const PREMIUM_FEATURES = [
  {
    icon: Brain,
    title: "MoodBot IA",
    description: "Chatbot personal que analiza tus patrones",
    color: "text-neon-purple",
    bgColor: "bg-neon-purple/20"
  },
  {
    icon: Heart,
    title: "Bienestar",
    description: "Dietas, ejercicios y tracking hormonal",
    color: "text-pink-400",
    bgColor: "bg-pink-500/20"
  },
  {
    icon: MessageCircle,
    title: "Chat Social",
    description: "Conecta con otros usuarios",
    color: "text-neon-cyan",
    bgColor: "bg-neon-cyan/20"
  },
  {
    icon: Trophy,
    title: "Gamificación",
    description: "Gana XP, niveles y logros",
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20"
  }
];

// Full page upgrade prompt for blocked features
export const UpgradePrompt = ({ 
  feature = "esta función",
  description = "Regístrate para desbloquear todas las funcionalidades",
  benefits = []
}) => {
  const navigate = useNavigate();

  const defaultBenefits = [
    "Sincroniza tus datos entre dispositivos",
    "Accede a recomendaciones de IA personalizadas",
    "Chatea con otros usuarios",
    "Guarda tu historial completo",
    "Recibe recordatorios personalizados"
  ];

  const displayBenefits = benefits.length > 0 ? benefits : defaultBenefits;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-center min-h-[60vh]"
    >
      <GlassCard className="p-8 max-w-lg text-center">
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-neon-purple" />
          </div>
          <h2 className="font-orbitron text-2xl text-white mb-2">
            {feature}
          </h2>
          <p className="text-slate-400 font-inter">
            {description}
          </p>
        </div>

        <div className="text-left space-y-3 mb-6 p-4 rounded-lg bg-white/5 border border-white/10">
          <p className="text-sm text-neon-cyan font-rajdhani font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Beneficios de registrarte:
          </p>
          <ul className="space-y-2">
            {displayBenefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="text-neon-green mt-0.5">✓</span>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => navigate("/login?action=register")}
            className="w-full cyber-button-cyan group"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Crear Cuenta Gratis
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-xs text-slate-500">
            Tus datos actuales se migrarán automáticamente
          </p>
        </div>
      </GlassCard>
    </motion.div>
  );
};

// Small inline prompt for restricted features
export const UpgradeBanner = ({ message = "Regístrate para desbloquear" }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-4 rounded-xl bg-gradient-to-r from-neon-purple/10 to-neon-cyan/10 border border-neon-purple/30 flex items-center justify-between gap-4"
    >
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-neon-purple" />
        <p className="text-sm text-slate-300">{message}</p>
      </div>
      <Button
        onClick={() => navigate("/login?action=register")}
        size="sm"
        className="bg-neon-purple/20 border border-neon-purple/50 text-neon-purple hover:bg-neon-purple/30"
      >
        Registrarse
      </Button>
    </motion.div>
  );
};

// NEW: Animated upgrade card with feature showcase
export const UpgradeShowcase = () => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  // Auto-rotate features
  useState(() => {
    const interval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % PREMIUM_FEATURES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  if (dismissed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative"
    >
      <GlassCard className="p-6 overflow-hidden relative">
        {/* Background gradient animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-neon-purple/5 via-neon-cyan/5 to-pink-500/5 animate-gradient-x" />
        
        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 text-slate-500 hover:text-white transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gradient-to-br from-neon-purple/30 to-neon-cyan/30">
              <Sparkles className="w-5 h-5 text-neon-cyan" />
            </div>
            <div>
              <h3 className="font-orbitron text-white text-sm">
                ¡Desbloquea todo MoodPunk!
              </h3>
              <p className="text-xs text-slate-400">
                Crea una cuenta gratis y accede a:
              </p>
            </div>
          </div>

          {/* Feature showcase - animated */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {PREMIUM_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = index === currentFeature;
              return (
                <motion.div
                  key={feature.title}
                  animate={{
                    scale: isActive ? 1.02 : 1,
                    borderColor: isActive ? 'rgba(0, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'
                  }}
                  className={`p-3 rounded-lg border transition-all cursor-pointer ${
                    isActive ? 'bg-white/10' : 'bg-white/5'
                  }`}
                  onClick={() => setCurrentFeature(index)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`p-1.5 rounded ${feature.bgColor}`}>
                      <Icon className={`w-3.5 h-3.5 ${feature.color}`} />
                    </div>
                    <span className={`text-xs font-rajdhani font-semibold ${isActive ? 'text-white' : 'text-slate-300'}`}>
                      {feature.title}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-1">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Benefits list */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-2 py-1 rounded-full bg-neon-green/10 text-neon-green text-xs flex items-center gap-1">
              <Zap className="w-3 h-3" /> 100% Gratis
            </span>
            <span className="px-2 py-1 rounded-full bg-neon-cyan/10 text-neon-cyan text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Datos migrados
            </span>
            <span className="px-2 py-1 rounded-full bg-pink-500/10 text-pink-400 text-xs flex items-center gap-1">
              <Bell className="w-3 h-3" /> Recordatorios
            </span>
          </div>

          {/* CTA Button */}
          <Button
            onClick={() => navigate("/login?action=register")}
            className="w-full bg-gradient-to-r from-neon-purple/30 to-neon-cyan/30 border border-neon-cyan/50 text-neon-cyan hover:from-neon-purple/40 hover:to-neon-cyan/40 group font-rajdhani"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Crear Cuenta Gratis
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <p className="text-center text-xs text-slate-600 mt-2">
            También puedes usar Google para registrarte
          </p>
        </div>
      </GlassCard>
    </motion.div>
  );
};

// Floating upgrade reminder (appears after some time)
export const FloatingUpgradeReminder = ({ delay = 30000 }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useState(() => {
    const timer = setTimeout(() => {
      if (!dismissed) setVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, dismissed]);

  if (!visible || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        className="fixed bottom-4 right-4 z-50 max-w-sm"
      >
        <div className="p-4 rounded-xl bg-void-paper border border-neon-cyan/30 shadow-lg shadow-neon-cyan/10">
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-neon-cyan/20">
              <Brain className="w-5 h-5 text-neon-cyan" />
            </div>
            <div>
              <p className="text-sm text-white font-rajdhani font-semibold mb-1">
                ¿Sabías que puedes tener un asistente de IA?
              </p>
              <p className="text-xs text-slate-400 mb-3">
                MoodBot analiza tus patrones y te da consejos personalizados.
              </p>
              <Button
                onClick={() => navigate("/login?action=register")}
                size="sm"
                className="bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan hover:bg-neon-cyan/30"
              >
                Activar MoodBot
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
